import React, { memo, useEffect, useState, useRef } from 'react';
import ListCard from '../Card/ListCard';
import Pagination from '../Pagination/Pagination';
import { updateAlgoliaViews } from '../../../../../util/api';
import css from './Hits.module.css';
import { H4, SkeletonCard } from '../../../../../components';
import { FormattedMessage } from '../../../../../util/reactIntl';

const ALGOLIA_LISTINGS_INDEX = process.env.REACT_APP_ALGOLIA_LISTINGS_INDEX;
const CONTACT_EMAIL = process.env.REACT_APP_TEAM_EMAIL || 'hey@rentanythingstore.com';
const CONTACT_PHONE = process.env.REACT_APP_TEAM_PHONE_NUMBER || '1-833-234-RENT';

const ContactAndMoreOptions = () => {
  return (
    <>
      <div className={css.lookingCard}>
        <div className={css.textSection}>
          <h2><FormattedMessage id="ContactAndMoreOptions.firstHeading" /></h2>
          <p>
            <FormattedMessage id="ContactAndMoreOptions.firstParagraph" />
          </p>
        </div>
        <div className={css.buttonSection}>
          <a href={`mailto:${CONTACT_EMAIL}`} className={css.primaryButton}>Contact Our Supply Team</a>
          <a href={`tel:${CONTACT_PHONE}`} className={css.secondaryButton}>Call Us: {CONTACT_PHONE}</a>
        </div>
      </div>
      <div className={css.moreOptionContent}>
        <h5><FormattedMessage id="ContactAndMoreOptions.secondHeading" /></h5>
        <p><FormattedMessage id="ContactAndMoreOptions.secondParagraph" /></p>
      </div>
    </>
  )
}

const CustomHits = props => {
  const {
    history,
    currentUser,
    routesConfiguration,
    currentUserCurrency,
    exchangeRates,
    onActivateListing,
    algoliaListings,
    queryID,
    algoliaQueryInProgress,
    algoliaQueryError,
    queryString,
    currentUserCountryCode
  } = props;

  const [currentRelevantPage, setCurrentRelevantPage] = useState(1);
  const [currentIrrelevantPage, setCurrentIrrelevantPage] = useState(1);
  const [updateViewsTimer, setUpdateViewsTimer] = useState(null);
  const pricesContainerRef = useRef(null);

  const relevantListingsRef = useRef(null);
  const irrelevantListingsRef = useRef(null);

  const relevantListings = algoliaListings.relevant || [];
  const irrelevantListings = algoliaListings.irrelevant || [];
  const productsPerPage = 24;

  const totalRelevantListings = relevantListings.length;
  const totalIrrelevantListings = irrelevantListings.length;

  const totalRelevantPages = Math.ceil(totalRelevantListings / productsPerPage);
  const totalIrrelevantPages = Math.ceil(totalIrrelevantListings / productsPerPage);

  // Reset pagination when query changes
  useEffect(() => {
    setCurrentRelevantPage(1);
    setCurrentIrrelevantPage(1);
  }, [queryString]);

  const handleRelevantPageChange = pageNumber => {
    if (pageNumber < 1 || pageNumber > totalRelevantPages) return;
    setCurrentRelevantPage(pageNumber);
    if (relevantListingsRef.current) {
      relevantListingsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleIrrelevantPageChange = pageNumber => {
    if (pageNumber < 1 || pageNumber > totalIrrelevantPages) return;
    setCurrentIrrelevantPage(pageNumber);
    if (irrelevantListingsRef.current) {
      irrelevantListingsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const indexOfLastRelevantProduct = currentRelevantPage * productsPerPage;
  const indexOfFirstRelevantProduct = indexOfLastRelevantProduct - productsPerPage;
  const renderableRelevantHits = relevantListings.slice(
    indexOfFirstRelevantProduct,
    indexOfLastRelevantProduct
  );

  const indexOfLastIrrelevantProduct = currentIrrelevantPage * productsPerPage;
  const indexOfFirstIrrelevantProduct = indexOfLastIrrelevantProduct - productsPerPage;
  const renderableIrrelevantHits = irrelevantListings.slice(
    indexOfFirstIrrelevantProduct,
    indexOfLastIrrelevantProduct
  );

  const hitsToRenderLength = relevantListings.length + irrelevantListings.length;

  useEffect(() => {
    if (updateViewsTimer) {
      clearTimeout(updateViewsTimer)
    };
    let timer;
    // Need to update the view count for each hit.
    const recordIds =
      algoliaListings && (relevantListings.length || irrelevantListings.length)
        ? [...relevantListings, ...irrelevantListings].map(h => h.objectID)
        : null;
    if (recordIds) {
      timer = setTimeout(() => {
        updateAlgoliaViews({
          indexName: ALGOLIA_LISTINGS_INDEX,
          recordIds,
        }).catch(err => console.log('Error:failed to update views'));
      }, 500);

      setUpdateViewsTimer(timer)
    };

    return () => clearTimeout(timer);
  }, [hitsToRenderLength]);

  if (algoliaQueryError) {
    return <H4 className={css.error}>{algoliaQueryError.message || 'failed to fetch results!'}</H4>;
  }


  const renderListCard = (item) => (
    <ListCard
      rental={item}
      key={item.objectID}
      currentUserCurrency={currentUserCurrency}
      exchangeRates={exchangeRates}
      onClick={() => {
        // sendEvent('click', i, PRODUCT_CLICKED_EVENT, {
        //   objectIds: [i.objectID],
        //   ...userTokenMaybe,
        // })
      }}
      history={history}
      routes={routesConfiguration}
      queryID={queryID}
      additional={item.additional}
      onActivateListing={onActivateListing}
      currentUserCountryCode={currentUserCountryCode}
    />
  );

  return (
    <>
      {algoliaQueryInProgress
        ? <div className={css.rentals} id="hits">
          {Array.from({ length: 10 }).map((_, index) => (
            <SkeletonCard pricesContainerRef={pricesContainerRef} className={css.hitSkeleton} key={index} />
          ))}
        </div> : (
          <>
            {/* Render relevant listings */}
            <div ref={relevantListingsRef}>
              {renderableRelevantHits.length > 0 && (
                <>
                  <div className={css.rentals} id="hits">
                    {renderableRelevantHits.map(renderListCard)}
                  </div>
                  {totalRelevantPages > 1 && (
                    <Pagination
                      totalPages={totalRelevantPages}
                      currentPage={currentRelevantPage}
                      onPageChange={handleRelevantPageChange}
                    />
                  )}
                </>
              )}
            </div>

            {/* Show contact and more options if we've shown all relevant listings */}
            {currentRelevantPage >= totalRelevantPages && renderableIrrelevantHits.length > 0 && (
              <>
                <ContactAndMoreOptions />
                {/* Render irrelevant listings */}
                <div ref={irrelevantListingsRef}>
                  {renderableIrrelevantHits.length > 0 && (
                    <>
                      <div className={css.rentals} id="hits">
                        {renderableIrrelevantHits.map(renderListCard)}
                      </div>
                      {totalIrrelevantPages > 1 && (
                        <Pagination
                          totalPages={totalIrrelevantPages}
                          currentPage={currentIrrelevantPage}
                          onPageChange={handleIrrelevantPageChange}
                        />
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
    </>
  );
};

export default memo(CustomHits);
